import type { ThemeConfig } from "antd";

/**
 * El tema del editor, tomado del sistema de Almia.
 *
 * Los valores NO son de cosecha propia: salen de
 * `landing-almia/src/index.css` y `Almia-TaskCRM/frontend/src/index.css`, que
 * los declaran en HSL. Aqui van en hex porque Ant Design necesita colores
 * concretos, pero son los mismos: si divergen, el creador de HV deja de
 * parecer parte del ecosistema.
 *
 * Correspondencia con el origen (modo claro):
 *
 *   --almia-purple        272 50% 55%   #9053C6
 *   --almia-purple-light  272 65% 93%   #EFE2FB
 *   --almia-purple-dark   272 41% 40%   #603C90
 *   --almia-lilac         272 55% 72%   #B48FDF
 *   --almia-mint          77 50% 55%    #A4C748
 *   --almia-mint-light    77 43% 90%    #E9F0D9
 *   --background          271 43% 98%   #FBF9FD
 *   --foreground          271 22% 18%   #2A2338
 *   --border              272 28% 87%   #DDD2E7
 *   --muted               271 49% 94%   #F1EAF7
 *   --muted-foreground    271 18% 45%   #6B5F7D
 *   --radius              0.75rem
 */

export const MARCA = {
  morado: "#9053C6",
  moradoOscuro: "#603C90",
  moradoClaro: "#EFE2FB",
  lila: "#B48FDF",
  menta: "#A4C748",
  mentaClara: "#E9F0D9",
} as const;

export const NEUTRO = {
  fondo: "#FBF9FD",
  superficie: "#FFFFFF",
  hundido: "#F1EAF7",
  borde: "#DDD2E7",
  tinta: "#2A2338",
  tintaSuave: "#6B5F7D",
  tintaTenue: "#9186A3",
} as const;

/**
 * El color de "aqui la IA cambio algo" es la MENTA de Almia, no un naranja.
 *
 * El naranja que usa el builder viejo no esta en el sistema: es un color suelto
 * de ese producto. La menta es el acento declarado del ecosistema y, ademas,
 * dice lo correcto: una sugerencia no es un error, y marcarla en rojo o naranja
 * la haria leer como si algo estuviera mal.
 */
export const SUGERENCIA = {
  senal: MARCA.menta,
  senalSuave: MARCA.mentaClara,
  aceptada: "#149389",
} as const;

const POPPINS = '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export const tema: ThemeConfig = {
  token: {
    colorPrimary: MARCA.morado,
    colorLink: MARCA.morado,
    colorSuccess: SUGERENCIA.aceptada,

    colorBgLayout: NEUTRO.fondo,
    colorBgContainer: NEUTRO.superficie,
    colorBorder: NEUTRO.borde,
    colorBorderSecondary: NEUTRO.borde,
    colorText: NEUTRO.tinta,
    colorTextSecondary: NEUTRO.tintaSuave,
    colorTextTertiary: NEUTRO.tintaTenue,

    fontFamily: POPPINS,
    fontSize: 14,

    // 0.75rem del sistema. El builder viejo y mi primera version usaban esquinas
    // casi rectas, que no es como se ve el resto del ecosistema.
    borderRadius: 12,
    wireframe: false,
  },

  components: {
    // El documento se ordena por espacio y tipografia, no por cajas: el radio
    // grande del sistema se reserva para controles, no para cada bloque.
    Card: { paddingLG: 20, boxShadowTertiary: "none" },
    Button: { fontWeight: 500, primaryShadow: "none", borderRadius: 10 },
    Input: { paddingBlock: 8, borderRadius: 10 },
    Tabs: { titleFontSize: 14 },
    Segmented: { itemSelectedBg: MARCA.moradoClaro, itemSelectedColor: MARCA.moradoOscuro },
    Tag: { borderRadiusSM: 6 },
  },
};
